import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { fetchAllPages, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type {
  CreateRunRequest,
  Deployment,
  DeploymentResource,
  OnFailure,
  ResourceType,
  RunSummary,
  SecretsOverview,
} from '../api/types';
import { Button, Dialog, ErrorMessage, Loading } from '../components/ui';
import { formatClock } from '../lib/format';
import { dangerousChanges } from '../lib/planReview';
import { reviewablePlan, useLatestPlan } from './plans';
import { RunChangesCell } from '../runs/RunSummaryTable';
import { strings } from '../strings';
import { vaultDeletionText } from '../variables/registry';

function FailurePolicy({ value, onChange }: { value: OnFailure; onChange: (value: OnFailure) => void }) {
  const id = useId();
  return (
    <div className="mt-4">
      <label className="label" htmlFor={id}>
        {strings.apply.onFailure}
      </label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value as OnFailure)}>
        <option value="stop">{strings.settings.stop}</option>
        <option value="rollback">{strings.settings.rollback}</option>
      </select>
      <p className="help">{value === 'stop' ? strings.settings.stopHelp : strings.settings.rollbackHelp}</p>
    </div>
  );
}

/** "Type “dev” to …" with its input (spec 6.4 typed guards). */
function TypedName({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="mt-3">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Apply (spec 6.4): "Apply this plan" for a reviewable plan, with its summary and dangerous changes; otherwise,
 * or as the alternative, an apply without a reviewed plan. Dangerous changes and unreviewed applies need the
 * deployment's name typed.
 */
export function ApplyDialog({
  deployment,
  latestVersion,
  plan: chosen,
  busy,
  onClose,
  onSubmit,
}: {
  deployment: Versioned<Deployment>;
  latestVersion: number;
  /** The plan to offer (the run page's plan); else the latest completed plan. */
  plan?: RunSummary;
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: CreateRunRequest) => void;
}) {
  const api = useApi();
  const d = deployment.data;
  const plan = useLatestPlan(d.id);
  const versions = useQuery({
    queryKey: ['blueprint', d.blueprintId, 'versions', 'all'],
    queryFn: () => fetchAllPages((page) => api.listVersions(d.blueprintId, page)),
  });
  const reviewed = reviewablePlan(chosen ?? plan.data, d, latestVersion);
  const planRun = useQuery({
    queryKey: queryKeys.run(reviewed?.id ?? ''),
    queryFn: () => api.getRun(reviewed!.id),
    enabled: !!reviewed,
  });
  const [mode, setMode] = useState<'reviewed' | 'unreviewed' | null>(null);
  const [version, setVersion] = useState(latestVersion);
  const [onFailure, setOnFailure] = useState<OnFailure>(d.settings.onFailure);
  const [typed, setTyped] = useState('');
  const versionId = useId();

  const effectiveMode = mode ?? (reviewed ? 'reviewed' : 'unreviewed');
  // Feature spec 15 (U2.1): without a reviewed plan, the dry run's deletion of a namespace vault's secrets.
  const dryRun = useQuery({
    queryKey: ['deployment', d.id, 'apply-dry-run', d.revision],
    queryFn: () => api.validateDeployment(d.blueprintId, { name: d.name, settings: d.settings }, d.id),
    enabled: effectiveMode === 'unreviewed' && d.vault?.state === 'live',
  });
  const dangers =
    effectiveMode === 'reviewed'
      ? dangerousChanges(
          planRun.data?.result?.plan,
          d.name,
          d.vault?.name,
          planRun.data?.result?.vaultSecretsDeletion ?? null,
        )
      : dangerousChanges(null, d.name, d.vault?.name, dryRun.data?.vaultSecretsDeletion ?? null);
  const loadingPlan = effectiveMode === 'reviewed' && planRun.isPending;
  // A plan that can't be loaded (or has no plan in its result) may hide dangerous changes: guarded (C7).
  const planUnknown =
    effectiveMode === 'reviewed' && (planRun.isError || (planRun.isSuccess && !planRun.data.result?.plan));
  const guarded = effectiveMode === 'unreviewed' || dangers.length > 0 || planUnknown;
  const ready = !busy && !plan.isPending && !loadingPlan && (!guarded || typed === d.name);

  const submit = () => {
    if (!ready) return;
    if (effectiveMode === 'reviewed' && reviewed)
      onSubmit({ kind: 'apply', expectedPlanRunId: reviewed.id, onFailure });
    else onSubmit({ kind: 'apply', blueprintVersion: version, onFailure });
  };

  return (
    <Dialog
      title={strings.apply.title(d.name)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="primary" disabled={!ready} onClick={submit}>
            {effectiveMode === 'reviewed' ? strings.apply.submitReviewed : strings.apply.submit}
          </Button>
        </>
      }
    >
      {plan.error ? (
        <ErrorMessage error={plan.error} />
      ) : plan.isPending ? (
        <Loading />
      ) : (
        <fieldset>
          <legend className="sr-only">{strings.apply.title(d.name)}</legend>
          {reviewed ? (
            <label className="mb-3 flex items-start gap-2">
              <input
                type="radio"
                name="apply-mode"
                className="mt-1"
                checked={effectiveMode === 'reviewed'}
                onChange={() => setMode('reviewed')}
              />
              <span>
                <span className="font-medium">{strings.apply.reviewed}</span>
                <span
                  className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm"
                  data-testid="apply-plan-summary"
                >
                  {strings.apply.reviewedSummary(
                    reviewed.blueprintVersion ?? latestVersion,
                    formatClock(reviewed.finishedAt),
                    reviewed.createdBy,
                  )}
                  <RunChangesCell run={reviewed} />
                </span>
                <span className="help block">{strings.apply.reviewedHelp}</span>
              </span>
            </label>
          ) : (
            <p className="tone-info mb-3 rounded-md border p-2 text-sm">{strings.apply.noPlan}</p>
          )}
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="apply-mode"
              className="mt-1"
              checked={effectiveMode === 'unreviewed'}
              onChange={() => setMode('unreviewed')}
            />
            <span>
              <span className="font-medium">{strings.apply.unreviewed}</span>
              <span className="help block">{strings.apply.unreviewedHelp}</span>
            </span>
          </label>
          {effectiveMode === 'unreviewed' && (
            <div className="mt-2 ml-6">
              <label className="label" htmlFor={versionId}>
                {strings.apply.version}
              </label>
              <select
                id={versionId}
                className="input w-40"
                value={version}
                onChange={(e) => setVersion(Number(e.target.value))}
              >
                {(versions.data ?? [{ version: latestVersion }]).map((v) => (
                  <option key={v.version} value={v.version}>
                    {v.version}
                  </option>
                ))}
              </select>
            </div>
          )}
        </fieldset>
      )}
      {loadingPlan && <p className="help mt-2">{strings.apply.loadingPlan}</p>}
      {planUnknown && (
        <div role="alert" className="callout callout-warning mt-3" data-testid="apply-plan-unknown">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p>{strings.apply.planUnknown}</p>
            {planRun.error && <ErrorMessage error={planRun.error} onRetry={() => void planRun.refetch()} />}
          </div>
        </div>
      )}
      {dangers.length > 0 && (
        <div role="alert" className="callout callout-danger mt-3" data-testid="apply-dangers">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div>
            <p className="font-semibold">{strings.apply.dangerTitle}</p>
            <ul className="mt-1 list-disc pl-5">
              {dangers.flatMap((danger) =>
                danger.sentences.map((sentence) => <li key={`${danger.id}-${sentence}`}>{sentence}</li>),
              )}
            </ul>
          </div>
        </div>
      )}
      {effectiveMode === 'unreviewed' && !plan.isPending && (
        <p className="callout callout-warning mt-3">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          {strings.apply.unreviewedGuard}
        </p>
      )}
      {guarded && !plan.isPending && (
        <TypedName label={strings.apply.typeName(d.name)} value={typed} onChange={setTyped} />
      )}
      <FailurePolicy value={onFailure} onChange={setOnFailure} />
    </Dialog>
  );
}

/**
 * Destroy (spec 6.4): the resources to delete with "data is deleted" next to stateful types, the vault's soft
 * delete from the location registry, and the deployment's name typed before the red button works.
 */
export function DestroyDialog({
  deployment,
  resources,
  types,
  vaultType,
  vaultOverview,
  busy,
  onClose,
  onConfirm,
  networkNote = null,
  blocked = null,
}: {
  deployment: Deployment;
  resources: readonly DeploymentResource[];
  types: ReadonlyMap<string, ResourceType>;
  /** The vault's location type from the vault overview (`azure-key-vault`). */
  vaultType: string | null | undefined;
  /** Feature spec 15: the overview (the retention and the unmanaged secrets under a namespace vault's prefix). */
  vaultOverview?: SecretsOverview | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Feature spec 17: what happens to the attachment of an attached deployment. */
  networkNote?: string | null;
  /** Feature spec 17, phase 7: why the destroy was refused (`ORIGIN_DETACH_BLOCKED`). */
  blocked?: ReactNode;
}) {
  const [typed, setTyped] = useState('');
  const [now] = useState(() => Date.now());
  const s = strings.destroy;
  const vault = deployment.vault?.state === 'live' ? deployment.vault : null;
  const deletion = vault
    ? vaultDeletionText(vaultType ?? null, { name: vault.name, region: vault.region }, deployment.name, now, {
        retentionDays: vaultOverview?.vault.deletedRetentionDays,
        otherSecrets: vaultOverview?.secrets.filter((x) => x.kind === 'unmanaged').length ?? 0,
      })
    : null;
  const vaultText = deletion?.text ?? null;
  return (
    <Dialog
      title={s.title(deployment.name)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy || typed !== deployment.name} onClick={onConfirm}>
            {s.submit(deployment.name)}
          </Button>
        </>
      }
    >
      <p className="mb-3">{s.intro(deployment.name)}</p>
      {blocked}
      {networkNote && (
        <p className="callout callout-warning mb-3" data-testid="destroy-network">
          {networkNote}
        </p>
      )}
      <h3 className="mb-1 text-sm font-semibold">{s.resources}</h3>
      {resources.length === 0 ? (
        <p className="help">{s.noResources}</p>
      ) : (
        <ul className="mb-3 space-y-1 text-sm" data-testid="destroy-resources">
          {resources.map((resource) => {
            const type = types.get(resource.type);
            return (
              <li key={resource.resourceId} className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-semibold">{resource.resourceId}</span>
                <span className="text-fg-muted">{type?.displayName ?? resource.type}</span>
                {type?.stateful && <span className="badge tone-danger">{s.dataDeleted}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {vaultText && (
        <p
          className={`callout ${deletion?.danger ? 'callout-danger' : 'callout-warning'} mb-3`}
          data-testid="destroy-vault"
        >
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          {vaultText}
        </p>
      )}
      <TypedName label={s.typeName(deployment.name)} value={typed} onChange={setTyped} />
    </Dialog>
  );
}

/** Delete and keep its secrets (feature spec 15 9.7): the typed name, then `DELETE …?keepVaultSecrets=true`. */
export function DeleteKeepDialog({
  deployment,
  text,
  busy,
  onClose,
  onConfirm,
}: {
  deployment: string;
  text: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  return (
    <Dialog
      title={strings.vaults.deleteKeepTitle(deployment)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy || typed !== deployment} onClick={onConfirm}>
            {strings.vaults.deleteKeepSubmit}
          </Button>
        </>
      }
    >
      <p className="callout callout-warning mb-3" data-testid="delete-keep-text">
        <TriangleAlert aria-hidden className="size-4 shrink-0" />
        {text}
      </p>
      <TypedName label={strings.vaults.typeName(deployment)} value={typed} onChange={setTyped} />
    </Dialog>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  onClose,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy} onClick={onConfirm} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
}
